import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

import { StatsCards } from "./admin/StatsCards";
import { TabBar } from "./admin/TabBar";
import { UsersTab } from "./admin/UsersTab";
import { AccessCodesTab } from "./admin/AccessCodesTab";
import { NotificationsTab } from "./admin/NotificationsTab";
import { SettingsTab } from "./admin/SettingsTab";
import { TemplatesTab } from "./admin/TemplatesTab";
import { DataToolsTab } from "./admin/DataToolsTab";
import { RecoveryTab } from "./admin/RecoveryTab";
import { CreateCodeDialog } from "./admin/dialogs/CreateCodeDialog";
import { ResetPasswordDialog } from "./admin/dialogs/ResetPasswordDialog";
import { DeleteUserDialog } from "./admin/dialogs/DeleteUserDialog";
import { EditUserDialog } from "./admin/dialogs/EditUserDialog";
import { useAdminData } from "./admin/useAdminData";

const API = process.env.REACT_APP_BACKEND_URL;

const ADMIN_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 } };

const EMPTY_EDIT = {
  open: false, user: null, name: '', email: '', password: '',
  storeName: '', storeAddress: '', subscriptionPlan: '', subscriptionStart: '',
  subscriptionEnd: '', subscriptionNotes: '', paymentMethod: '',
  checkNumber: '', paymentAmount: '', paymentDate: '',
};

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const onUnauthorized = useCallback(() => navigate("/entry"), [navigate]);
  const isAdmin = user?.role === "admin";

  const {
    users, accessCodes, recoveryPhrase, packSizes, supportContact, notifications, unreadCount, loading,
    setPackSizes, setRecoveryPhrase, setSupportContact, fetchUsers, fetchAccessCodes, markNotificationsRead,
  } = useAdminData(isAdmin, onUnauthorized);

  const [activeTab, setActiveTab] = useState('users');
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingSupport, setSavingSupport] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, step: 0, confirmText: '' });
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, newPassword: '' });
  const [newCodeDialog, setNewCodeDialog] = useState({ open: false, code: '', maxUses: 1 });
  const [editDialog, setEditDialog] = useState(EMPTY_EDIT);

  useEffect(() => {
    if (!isAdmin) navigate("/entry");
  }, [isAdmin, navigate]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'notifications' && unreadCount > 0) markNotificationsRead();
  };

  // === User Actions ===
  const toggleActive = async (userId) => {
    try {
      const res = await axios.put(`${API}/api/admin/users/${userId}/toggle-active`);
      toast.success(res.data.is_active ? "User unlocked" : "User locked");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const handleResetPassword = async () => {
    if (resetDialog.newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    try {
      await axios.put(`${API}/api/admin/users/${resetDialog.user.id}/reset-password`, {
        new_password: resetDialog.newPassword,
      });
      toast.success(`Password reset for ${resetDialog.user.email}`);
      setResetDialog({ open: false, user: null, newPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleResetPin = async (u) => {
    if (!u) return;
    if (!window.confirm(`Clear the 4-digit Settings PIN for ${u.email}? They will be asked to set a new one next time they open Settings.`)) return;
    try {
      await axios.put(`${API}/api/admin/users/${u.id}/reset-pin`);
      toast.success(`PIN cleared for ${u.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear PIN");
    }
  };

  const handleDeleteConfirm = async () => {
    const u = deleteDialog.user;
    const targetEmail = (u?.email || '').trim().toLowerCase();
    if (deleteDialog.confirmText.trim().toLowerCase() !== targetEmail) return;
    try {
      const res = await axios.delete(`${API}/api/admin/users/${u.id}`, {
        params: { confirm_email: u?.email },
      });
      const cnt = res.data?.deleted_entries ?? 0;
      toast.success(`User deleted — ${cnt} entries moved to Recovery (restorable for 30 days)`);
      setDeleteDialog({ open: false, user: null, step: 0, confirmText: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const handlePauseUser = async (u) => {
    try {
      await axios.put(`${API}/api/admin/users/${u.id}/pause`, {
        is_paused: true,
        pause_until: null,
      });
      toast.success(`${u.name} paused — view-only until you activate them`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pause user");
    }
  };

  const handleUnpauseUser = async (u) => {
    try {
      await axios.put(`${API}/api/admin/users/${u.id}/pause`, {
        is_paused: false,
        pause_until: null,
      });
      toast.success(`${u.name} activated — data entry restored`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate user");
    }
  };

  const handleEditUser = async () => {
    const updates = {};
    if (editDialog.name && editDialog.name !== editDialog.user?.name) updates.name = editDialog.name;
    if (editDialog.email && editDialog.email !== editDialog.user?.email) updates.email = editDialog.email;
    if (editDialog.password) updates.password = editDialog.password;
    if (editDialog.storeName !== (editDialog.user?.store_name || '')) updates.store_name = editDialog.storeName;
    if (editDialog.storeAddress !== (editDialog.user?.store_address || '')) updates.store_address = editDialog.storeAddress;
    updates.subscription = {
      plan: editDialog.subscriptionPlan,
      start_date: editDialog.subscriptionStart,
      end_date: editDialog.subscriptionEnd,
      notes: editDialog.subscriptionNotes,
    };
    try {
      await axios.put(`${API}/api/admin/users/${editDialog.user.id}/credentials`, updates);
      toast.success(`Updated info for ${editDialog.user.name}`);
      setEditDialog(EMPTY_EDIT);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const openEdit = (u) =>
    setEditDialog({
      open: true,
      user: u,
      name: u.name,
      email: u.email,
      password: '',
      storeName: u.store_name || '',
      storeAddress: u.store_address || '',
      subscriptionPlan: u.subscription?.plan || '',
      subscriptionStart: u.subscription?.start_date || '',
      subscriptionEnd: u.subscription?.end_date || '',
      subscriptionNotes: u.subscription?.notes || '',
      paymentMethod: '',
      checkNumber: '',
      paymentAmount: '',
      paymentDate: '',
    });

  // === Access Codes ===
  const createAccessCode = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/access-codes`, {
        code: newCodeDialog.code || undefined,
        max_uses: parseInt(newCodeDialog.maxUses) || 1,
      });
      toast.success(`Access code created: ${res.data.code}`);
      setNewCodeDialog({ open: false, code: '', maxUses: 1 });
      fetchAccessCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create code");
    }
  };

  const deleteAccessCode = async (codeId) => {
    try {
      await axios.delete(`${API}/api/admin/access-codes/${codeId}`);
      toast.success("Code deleted");
      fetchAccessCodes();
    } catch (err) {
      toast.error("Failed to delete code");
    }
  };

  const toggleAccessCode = async (codeId) => {
    try {
      const res = await axios.put(`${API}/api/admin/access-codes/${codeId}/toggle`);
      toast.success(res.data.is_active ? "Code enabled" : "Code disabled");
      fetchAccessCodes();
    } catch (err) {
      toast.error("Failed to update code");
    }
  };

  // === Recovery Phrase ===
  const saveRecoveryPhrase = async (phrase) => {
    if (phrase.length < 6) {
      toast.error("Recovery phrase must be at least 6 characters");
      return false;
    }
    try {
      await axios.put(`${API}/api/admin/recovery-phrase`, { recovery_phrase: phrase });
      toast.success("Recovery phrase saved");
      setRecoveryPhrase(true);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save recovery phrase");
      return false;
    }
  };

  // === Settings ===
  const DENOMINATIONS = [1, 2, 3, 5, 10, 20, 30, 50];
  const savePackSizes = async () => {
    setSavingSettings(true);
    try {
      const sized = {};
      DENOMINATIONS.forEach((d) => {
        sized[String(d)] = parseInt(packSizes[d]) || 30;
      });
      await axios.put(`${API}/api/admin/settings`, { pack_sizes: sized });
      toast.success("Pack sizes saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveSupportContact = async () => {
    setSavingSupport(true);
    try {
      await axios.put(`${API}/api/admin/settings`, { support_contact: supportContact });
      toast.success("Support contact updated — users will see the new info on next page load.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save support contact");
    } finally {
      setSavingSupport(false);
    }
  };

  // === Misc ===
  const copyCode = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => toast.success("Code copied!"))
      .catch(() => {
        const ta = document.createElement('textarea');
        ta.value = code;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success("Code copied!");
      });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>
    );
  }

  return (
    <motion.div {...ADMIN_FADE} className="space-y-6">
      <StatsCards users={users} />

      <TabBar activeTab={activeTab} onChange={handleTabChange} unreadCount={unreadCount} />

      {activeTab === 'users' && (
        <UsersTab
          users={users}
          onToggleActive={toggleActive}
          onResetPassword={(u) => setResetDialog({ open: true, user: u, newPassword: '' })}
          onResetPin={handleResetPin}
          onDelete={(u) => setDeleteDialog({ open: true, user: u, step: 1, confirmText: '' })}
          onPause={handlePauseUser}
          onUnpause={handleUnpauseUser}
          onEdit={openEdit}
        />
      )}

      {activeTab === 'codes' && (
        <AccessCodesTab
          accessCodes={accessCodes}
          recoveryPhrase={recoveryPhrase}
          onCreateCode={() => setNewCodeDialog({ open: true, code: '', maxUses: 1 })}
          onToggleCode={toggleAccessCode}
          onDeleteCode={deleteAccessCode}
          onSaveRecoveryPhrase={saveRecoveryPhrase}
          onCopy={copyCode}
        />
      )}

      {activeTab === 'notifications' && <NotificationsTab notifications={notifications} />}

      {activeTab === 'templates' && <TemplatesTab />}

      {activeTab === 'data-tools' && <DataToolsTab />}

      {activeTab === 'recovery' && <RecoveryTab />}

      {activeTab === 'settings' && (
        <SettingsTab
          packSizes={packSizes}
          onPackSizesChange={setPackSizes}
          onSave={savePackSizes}
          saving={savingSettings}
          supportContact={supportContact}
          onSupportContactChange={setSupportContact}
          onSaveSupportContact={saveSupportContact}
          savingSupport={savingSupport}
        />
      )}

      <CreateCodeDialog
        state={newCodeDialog}
        onChange={setNewCodeDialog}
        onClose={() => setNewCodeDialog({ open: false, code: '', maxUses: 1 })}
        onConfirm={createAccessCode}
      />

      <ResetPasswordDialog
        state={resetDialog}
        onChange={setResetDialog}
        onClose={() => setResetDialog({ open: false, user: null, newPassword: '' })}
        onConfirm={handleResetPassword}
      />

      <DeleteUserDialog
        state={deleteDialog}
        onChange={setDeleteDialog}
        onClose={() => setDeleteDialog({ open: false, user: null, step: 0, confirmText: '' })}
        onConfirm={handleDeleteConfirm}
      />

      <EditUserDialog
        state={editDialog}
        onChange={setEditDialog}
        onClose={() => setEditDialog(EMPTY_EDIT)}
        onSave={handleEditUser}
      />
    </motion.div>
  );
}
